import React from "react";
import { Milestone, SkillGroup, ManifestoPoint, EvolutionStep } from "../data/profileData";
import { useLanguage } from "../context/LanguageContext";
import "./../css/whoami.scss";

const Hero: React.FC<{ data: any; title: string }> = ({ data, title }) => (
  <div className="row mb-4">
    <div className="col-12 p-2">
      <div className="d-flex flex-column">
        <span className="fw-bold section-title">{title}</span>
        <p className="mt-3 about-text">{data.hero.about}</p>
      </div>
    </div>
  </div>
);

const EvolutionPath: React.FC<{ title: string; steps: EvolutionStep[] }> = ({ title, steps }) => (
  <div className="row mb-4">
    <div className="col-12 p-2">
      <span className="fw-bold section-title">{title}</span>
      <div className="mt-3">
        {steps.map((step, i) => (
          <div key={i} className="mb-3 evolution-item">
            <h6 className="fw-bold mb-0">
              [{step.period}] {step.stage}
            </h6>
            <p className="mb-0 italic">{step.context}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SystemMilestones: React.FC<{ title: string; logicLabel: string; milestones: Milestone[] }> = ({ title, logicLabel, milestones }) => (
  <div className="row mb-4">
    <div className="col-12 p-2">
      <span className="fw-bold section-title">{title}</span>
      <div className="mt-3">
        {milestones.map((m, i) => (
          <div key={i} className="mb-3 milestone-item">
            <h6 className="fw-bold mb-1">{'>>'} {m.title}</h6>
            <p className="mb-1">{m.description}</p>
            <p className="mb-0 impact-text"><strong>{logicLabel}:</strong> {m.logicResults}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const StackRenderer: React.FC<{ title: string; groups: SkillGroup[]; className?: string }> = ({ title, groups, className }) => (
  <div className={`row mb-4 ${className}`}>
    <div className="col-12 p-2">
      <span className="fw-bold section-title">{title}</span>
      <div className="mt-3">
        {groups.map((group, i) => (
          <div key={i} className="mb-2">
            <h6 className="mb-1 stack-category">{group.category}</h6>
            <div className="d-flex flex-wrap gap-2">
              {group.skills.map((skill, si) => (
                <span key={si} className="badge bg-dark border border-secondary">{skill}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Manifesto: React.FC<{ title: string; points: ManifestoPoint[] }> = ({ title, points }) => (
  <div className="row mb-4">
    <div className="col-12 p-2">
      <span className="fw-bold section-title">{title}</span>
      <div className="mt-3">
        {points.map((p, i) => (
          <div key={i} className="mb-2">
            <h6 className="mb-0 fw-bold text-success text-uppercase" style={{ fontSize: '0.8rem' }}>{p.principle}</h6>
            <p className="mb-1 italic">{p.statement}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

function Whoami() {
  const { data } = useLanguage();

  return (
    <div className="px-2 py-2 whoami-engine">
      <div className="info-container px-3">
        <Hero data={data} title={data.ui.aboutTitle} />
        <Manifesto title={data.ui.manifestoTitle} points={data.manifesto} />
        <EvolutionPath title={data.ui.evolutionTitle} steps={data.evolution} />
        <SystemMilestones title={data.ui.systemMilestones} logicLabel={data.ui.logicResults} milestones={data.milestones} />
        <StackRenderer title={data.ui.stackMetal} groups={data.infrastructureStack} />
        <StackRenderer title={data.ui.stackIntelligence} groups={data.intelligenceStack} />
        <StackRenderer title={data.ui.stackLegacy} groups={data.legacyStack} className="legacy-section" />
      </div>
      <div className="more-info mt-4 d-flex">
        <p className="mb-0">
          {data.ui.analysisComplete} {data.ui.helpTip.split('help')[0]}<span className="fw-bold text-success">help</span>{data.ui.helpTip.split('help')[1]}
        </p>
      </div>
    </div>
  );
}

export default Whoami;